import { Package } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateTimePicker } from '@nucleus-commerce/admin-kit/date-time';
import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { AdminAuction } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { fromZonedInput } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { ProductPicker } from '@/features/resource-pickers';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  auctionToForm,
  emptyAuctionForm,
  mapAuctionField,
  toCreateAuction,
  toUpdateAuction,
  type SaveAs,
} from '../api/mappers';
import { useCreateAuction, useUpdateAuction } from '../hooks/use-auction-mutations';
import { useLotVariants } from '../hooks/use-lot-variants';
import {
  auctionFormSchema,
  type AuctionFormInput,
  type AuctionFormOutput,
} from '../schemas/auction-form-schema';

import { IncrementTableEditor } from './increment-table-editor';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'storeProductId',
  'variantId',
  'startsAt',
  'endsAt',
  'startingPrice',
  'reservePrice',
  'buyNowPrice',
  'incrementTable',
  'antiSnipeMinutes',
  'buyerPremiumPercent',
  ...Array.from({ length: 20 }, (_, i) => [
    `incrementTable.${i}.from`,
    `incrementTable.${i}.step`,
  ]).flat(),
]);

/** 409s with no field: said plainly where the form shows its errors. */
const CONFLICT_COPY: Record<string, string> = {
  PRODUCT_IN_AUCTION: 'auctions:errors.productInAuction',
  INSUFFICIENT_STOCK: 'auctions:errors.insufficientStock',
  AUCTION_NOT_EDITABLE: 'auctions:errors.notEditable',
};

export interface AuctionLotPrefill {
  storeProductId: string;
  title: string;
  variantId?: string;
}

export interface AuctionFormDialogProps {
  storeId: string;
  currency: string;
  timeZone: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit this auction (draft or scheduled); omit to create one. */
  auction?: AdminAuction;
  /** Create with this store product as the lot ("Sell as auction"). */
  lot?: AuctionLotPrefill;
  /** Create the auction as a draft lot of this draft sale; the sale sets when bidding runs. */
  saleId?: string;
  onSaved?: (auction: AdminAuction) => void;
}

/**
 * Creates or edits an auction. The lot is picked from the store's products, then one variant;
 * times are typed on the store's clock; the reserve is hidden from shoppers; the increment table
 * starts from the defaults. Saving as a draft keeps the product on sale; scheduling withdraws it
 * and holds one unit.
 */
export function AuctionFormDialog({
  storeId,
  currency,
  timeZone,
  open,
  onOpenChange,
  auction,
  lot,
  saleId,
  onSaved,
}: AuctionFormDialogProps) {
  const { t } = useTranslation(['auctions', 'common']);
  const format = useFormat();
  const message = useErrorMessage();
  const create = useCreateAuction(storeId);
  const update = useUpdateAuction(storeId);
  const [picking, setPicking] = useState(false);
  const [lotTitle, setLotTitle] = useState<string | null>(auction?.lot.title ?? lot?.title ?? null);
  const defaults = () =>
    auction ? auctionToForm(auction, timeZone) : emptyAuctionForm(currency, timeZone, lot ?? null);
  const form = useForm<AuctionFormInput, unknown, AuctionFormOutput>({
    resolver: useI18nResolver(
      auctionFormSchema((value) => {
        const iso = fromZonedInput(value, timeZone);
        return iso ? Date.parse(iso) : null;
      }),
    ),
    defaultValues: defaults(),
  });
  const [storeProductId, variantId] = useWatch({
    control: form.control,
    name: ['storeProductId', 'variantId'],
  });
  const variants = useLotVariants(storeId, auction ? null : storeProductId || null);

  // One variant: it is the lot, nothing to choose.
  const only = variants.data?.length === 1 ? variants.data[0] : undefined;
  useEffect(() => {
    if (only && !variantId) form.setValue('variantId', only.variantId, { shouldValidate: true });
  }, [form, only, variantId]);

  const pending = create.isPending || update.isPending;

  const close = () => {
    onOpenChange(false);
    create.reset();
    update.reset();
    form.reset(defaults());
    setLotTitle(auction?.lot.title ?? lot?.title ?? null);
  };

  const save = (saveAs: SaveAs) =>
    form.handleSubmit(async (values) => {
      try {
        const saved = auction
          ? await update.mutateAsync({
              id: auction.id,
              body: toUpdateAuction(values, saveAs, timeZone),
            })
          : await create.mutateAsync(toCreateAuction(values, saveAs, timeZone, saleId));
        onSaved?.(saved);
        close();
      } catch (error) {
        if (
          applyFieldErrors(error, form.setError, {
            knownFields: KNOWN_FIELDS,
            mapField: mapAuctionField,
          })
        ) {
          return;
        }
        const conflict = error instanceof ApiError ? CONFLICT_COPY[error.code] : undefined;
        form.setError('root.server', {
          type: 'server',
          message: conflict ? t(conflict) : message(error),
        });
      }
    })();

  const lotError =
    form.formState.errors.storeProductId?.message ?? form.formState.errors.variantId?.message;
  const isScheduled = auction?.status === 'scheduled';

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent
        className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-2xl"
        data-tour="auctions.form.dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {auction ? t('auctions:form.editTitle') : t('auctions:form.title')}
          </DialogTitle>
          <DialogDescription>
            {auction ? t('auctions:form.editDescription') : t('auctions:form.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            noValidate
            className="flex flex-col gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              void save(isScheduled ? 'scheduled' : 'draft');
            }}
          >
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}

            <section className="flex flex-col gap-3" aria-labelledby="auction-lot-heading">
              <h3 id="auction-lot-heading" className="text-sm font-semibold">
                {t('auctions:form.lot')}
              </h3>
              <div className="flex flex-wrap items-center gap-3 rounded-md border p-3">
                <Package aria-hidden className="text-muted-foreground size-5" />
                <span
                  className="min-w-0 flex-1 truncate text-sm font-medium"
                  data-testid="lot-title"
                >
                  {lotTitle ?? t('auctions:form.noLot')}
                </span>
                {auction ? null : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPicking(true)}
                  >
                    {lotTitle ? t('auctions:form.changeLot') : t('auctions:form.chooseLot')}
                  </Button>
                )}
              </div>
              {auction ? (
                <p className="text-muted-foreground text-xs">
                  {t('auctions:form.lotFixed', { variant: auction.lot.variantTitle ?? '—' })}
                </p>
              ) : storeProductId ? (
                <FormField
                  control={form.control}
                  name="variantId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auctions:fields.variant')}</FormLabel>
                      <Select
                        value={field.value || undefined}
                        onValueChange={field.onChange}
                        disabled={variants.isPending}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t('auctions:fields.variantPlaceholder')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(variants.data ?? []).map((variant) => (
                            <SelectItem key={variant.variantId} value={variant.variantId}>
                              {variant.title || t('auctions:lot.noVariant')}
                              {variant.effective ? ` · ${format.money(variant.effective)}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>{t('auctions:fields.variantHint')}</FormDescription>
                    </FormItem>
                  )}
                />
              ) : null}
              {lotError ? (
                <p role="alert" className="text-destructive text-sm">
                  {lotError}
                </p>
              ) : null}
            </section>

            {saleId ? (
              <p className="text-muted-foreground text-xs" data-testid="auction-sale-times">
                {t('auctions:form.saleSetsTimes')}
              </p>
            ) : null}
            <section
              className="flex flex-col gap-3"
              aria-labelledby="auction-times-heading"
              hidden={Boolean(saleId)}
            >
              <h3 id="auction-times-heading" className="text-sm font-semibold">
                {t('auctions:form.times')}
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="startsAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auctions:fields.startsAt')}</FormLabel>
                      <FormControl>
                        <DateTimePicker timeZone={timeZone} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="endsAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auctions:fields.endsAt')}</FormLabel>
                      <FormControl>
                        <DateTimePicker timeZone={timeZone} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <p className="text-muted-foreground text-xs" data-testid="auction-time-zone">
                {t('auctions:fields.timeZoneHint', { zone: timeZone })}
              </p>
            </section>

            <section className="flex flex-col gap-3" aria-labelledby="auction-prices-heading">
              <h3 id="auction-prices-heading" className="text-sm font-semibold">
                {t('auctions:form.prices')}
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="startingPrice"
                  render={({ field, fieldState }) => (
                    <FormItem data-tour="auctions.form.starting-price">
                      <FormLabel>{t('auctions:fields.startingPrice')}</FormLabel>
                      <FormControl>
                        <MoneyInput
                          currency={currency}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          aria-invalid={fieldState.invalid}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="reservePrice"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t('auctions:fields.reservePrice')}</FormLabel>
                      <FormControl>
                        <MoneyInput
                          currency={currency}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          aria-invalid={fieldState.invalid}
                        />
                      </FormControl>
                      <FormDescription>{t('auctions:fields.reserveHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="buyNowPrice"
                  render={({ field, fieldState }) => (
                    <FormItem data-tour="auctions.form.buy-now">
                      <FormLabel>{t('auctions:fields.buyNowPrice')}</FormLabel>
                      <FormControl>
                        <MoneyInput
                          currency={currency}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          aria-invalid={fieldState.invalid}
                        />
                      </FormControl>
                      <FormDescription>{t('auctions:fields.buyNowHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <IncrementTableEditor currency={currency} />
            </section>

            <section className="grid gap-3 sm:grid-cols-2" aria-label={t('auctions:form.rules')}>
              <FormField
                control={form.control}
                name="antiSnipeMinutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auctions:fields.antiSnipe')}</FormLabel>
                    <FormControl>
                      <Input inputMode="numeric" {...field} />
                    </FormControl>
                    <FormDescription>{t('auctions:fields.antiSnipeHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="buyerPremiumPercent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auctions:fields.premium')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input inputMode="decimal" className="pr-8" {...field} />
                        <span
                          aria-hidden
                          className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm"
                        >
                          %
                        </span>
                      </div>
                    </FormControl>
                    <FormDescription>{t('auctions:fields.premiumHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </section>

            <DialogFooter className="gap-2 sm:justify-between">
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                {saleId ? (
                  <Button type="button" disabled={pending} onClick={() => void save('draft')}>
                    {pending ? t('auctions:form.saving') : t('auctions:form.addToSale')}
                  </Button>
                ) : isScheduled ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={pending}
                      onClick={() => void save('draft')}
                    >
                      {t('auctions:form.moveToDraft')}
                    </Button>
                    <Button type="button" disabled={pending} onClick={() => void save('scheduled')}>
                      {pending ? t('auctions:form.saving') : t('auctions:form.save')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={pending}
                      onClick={() => void save('draft')}
                    >
                      {t('auctions:form.saveDraft')}
                    </Button>
                    <Button
                      type="button"
                      disabled={pending}
                      onClick={() => void save('scheduled')}
                      data-tour="auctions.form.schedule"
                    >
                      {pending ? t('auctions:form.saving') : t('auctions:form.schedule')}
                    </Button>
                  </>
                )}
              </div>
            </DialogFooter>
          </form>
        </Form>
        {auction ? null : (
          <ProductPicker
            open={picking}
            onOpenChange={setPicking}
            scope={{ kind: 'store', storeId }}
            multiple={false}
            title={t('auctions:form.pickTitle')}
            confirmLabel={t('auctions:form.pickConfirm')}
            onConfirm={(selection) => {
              const picked = selection.items[0];
              if (!picked) return;
              form.setValue('storeProductId', picked.id, { shouldValidate: true });
              form.setValue('variantId', '');
              form.clearErrors(['storeProductId', 'variantId']);
              setLotTitle(picked.title ?? picked.product.title);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
